import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getCategories, createCategory, updateCategory, deleteCategory, Category } from '../api/products.api';
import LoadingSpinner from '../components/LoadingSpinner';
import DeleteCategoryModal from '../components/DeleteCategoryModal';

const AdminCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; categoryId: string | null; categoryName: string; productCount?: number }>({
    show: false,
    categoryId: null,
    categoryName: '',
  });
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    display_order: 0,
    is_active: true,
    service_slug: '',
    parent_category_id: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCategories();
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const VALID_SERVICE_SLUGS = [
    'customised-jersey',
    'apparel-production',
    'sports-item',
    'award-and-trophy',
    'hunting-program',
    'sports-events',
    'dealership-program'
  ];

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      display_order: 0,
      is_active: true,
      service_slug: '',
      parent_category_id: '',
    });
    setErrors({});
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (category: Category) => {
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      display_order: category.display_order,
      is_active: category.is_active,
      service_slug: category.service_slug || '',
      parent_category_id: category.parent_category_id || '',
    });
    setErrors({});
    setEditingCategory(category);
  };

  const handleCloseModals = () => {
    setShowCreateModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      display_order: 0,
      is_active: true,
      service_slug: '',
      parent_category_id: '',
    });
    setErrors({});
  };

  const handleChange = (field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
    }));
    if (errors.name) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.name;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required.';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        display_order: formData.display_order,
        is_active: formData.is_active,
      };

      // Only include service_slug if provided
      if (formData.service_slug) {
        payload.service_slug = formData.service_slug;
      } else {
        payload.service_slug = null;
      }

      // Only include parent_category_id if provided
      if (formData.parent_category_id) {
        payload.parent_category_id = formData.parent_category_id;
      } else {
        payload.parent_category_id = null;
      }

      if (editingCategory) {
        await updateCategory(editingCategory.id, payload);
      } else {
        await createCategory(payload);
      }
      handleCloseModals();
      fetchCategories();
    } catch (err: any) {
      if (err.response?.status === 400) {
        const errorData = err.response?.data;
        if (errorData?.errors) {
          setErrors(errorData.errors);
        } else {
          setErrors({ general: errorData?.message || 'Invalid data. Please check your inputs.' });
        }
      } else {
        setErrors({ general: err.response?.data?.message || 'Failed to save category. Please try again.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.categoryId) return;

    try {
      await deleteCategory(deleteModal.categoryId);
      setDeleteModal({ show: false, categoryId: null, categoryName: '' });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="mb-3">
        <Link to="/dashboard" className="btn btn-outline-secondary btn-sm">
          <i className="bi bi-arrow-left me-1"></i>
          Back to Dashboard
        </Link>
      </div>
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">CATEGORY MANAGEMENT</h1>
          <p className="text-muted-soft mb-0">Manage product categories</p>
        </div>
        <button className="btn btn-arena" onClick={handleOpenCreateModal}>
          <i className="bi bi-plus-circle me-2"></i>
          Add Category
        </button>
      </div>

      <div className="card card-surface">
        <div className="card-body">
          {categories.length === 0 ? (
            <div className="alert alert-secondary mb-0">No categories found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Products</th>
                    <th>Display Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id}>
                      <td className="fw-semibold">{category.name}</td>
                      <td>
                        <code className="text-muted-soft">{category.slug}</code>
                      </td>
                      <td>{category.product_count || 0}</td>
                      <td>{category.display_order}</td>
                      <td>
                        <span className={`badge ${category.is_active ? 'bg-success' : 'bg-secondary'} badge-status`}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleOpenEditModal(category)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() =>
                              setDeleteModal({
                                show: true,
                                categoryId: category.id,
                                categoryName: category.name,
                                productCount: category.product_count,
                              })
                            }
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCategory) && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={handleCloseModals}
            style={{ zIndex: 1040 }}
          ></div>
          <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content card-surface">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">{editingCategory ? 'Edit Category' : 'Create Category'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModals}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {errors.general && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.general}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => {
                            const newErrors = { ...errors };
                            delete newErrors.general;
                            setErrors(newErrors);
                          }}
                          aria-label="Close"
                        ></button>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">
                        Category Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        value={formData.name}
                        onChange={(e) => handleNameChange(e.target.value)}
                        disabled={saving}
                        maxLength={255}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.slug ? 'is-invalid' : ''}`}
                        value={formData.slug}
                        onChange={(e) => handleChange('slug', e.target.value)}
                        disabled={saving}
                        placeholder="category-slug"
                      />
                      {errors.slug && <div className="invalid-feedback">{errors.slug}</div>}
                      <div className="form-text">URL-friendly identifier (auto-generated from name)</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        disabled={saving}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Display Order</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.display_order}
                        onChange={(e) => handleChange('display_order', parseInt(e.target.value) || 0)}
                        disabled={saving}
                      />
                      <div className="form-text">Lower numbers appear first</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Service Slug</label>
                      <select
                        className={`form-select ${errors.service_slug ? 'is-invalid' : ''}`}
                        value={formData.service_slug}
                        onChange={(e) => handleChange('service_slug', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">None (No Service)</option>
                        {VALID_SERVICE_SLUGS.map((slug) => (
                          <option key={slug} value={slug}>
                            {slug.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                          </option>
                        ))}
                      </select>
                      {errors.service_slug && <div className="invalid-feedback">{errors.service_slug}</div>}
                      <div className="form-text">Assign this category to a main service</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Parent Category</label>
                      <select
                        className={`form-select ${errors.parent_category_id ? 'is-invalid' : ''}`}
                        value={formData.parent_category_id}
                        onChange={(e) => handleChange('parent_category_id', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">None (Top Level)</option>
                        {categories
                          .filter((cat) => cat.id !== editingCategory?.id)
                          .map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                      </select>
                      {errors.parent_category_id && <div className="invalid-feedback">{errors.parent_category_id}</div>}
                      <div className="form-text">Optional: Assign a parent category for nested hierarchies</div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="is_active"
                          checked={formData.is_active}
                          onChange={(e) => handleChange('is_active', e.target.checked)}
                          disabled={saving}
                        />
                        <label className="form-check-label" htmlFor="is_active">
                          Category is active
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCloseModals}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-arena" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        editingCategory ? 'Update Category' : 'Create Category'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteCategoryModal
        show={deleteModal.show}
        categoryName={deleteModal.categoryName}
        productCount={deleteModal.productCount}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ show: false, categoryId: null, categoryName: '' })}
      />
    </div>
  );
};

export default AdminCategoriesPage;
